import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, Col, Row, Button, Form } from 'react-bootstrap';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { format, addDays } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import paths from 'routes/paths';
import {
  FormProvider,
  RHFControl,
  RHFDatePicker,
  RHFSelect,
  DraftPrompt,
  DraftSavedNote
} from 'components/common/form';
import LoadingButton from 'components/common/LoadingButton';
import Flex from 'components/common/Flex';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import { useAuthContext } from 'context/authContext';
import {
  useSchoolStudents,
  useCreateSchoolReport,
  useUpdateSchoolReport
} from 'hooks/useBeneficiaries';
import { useCreateReminder, useOrganisationUsers, useBranchAdmins } from 'hooks/useReminders';
import useUpload from 'hooks/useUpload';
import useFormDraft from 'hooks/useFormDraft';
import SchoolReportPDF from './SchoolReportPDF';
import {
  schoolReportDraftStore,
  schoolReportDraftScope
} from './schoolReportDraft';

const gradeOptions = [
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Good', label: 'Good' },
  { value: 'Average', label: 'Average' },
  { value: 'Below Average', label: 'Below Average' },
  { value: 'Poor', label: 'Poor' }
];

const schema = Yup.object().shape({
  reportDate: Yup.string().required('Report date is required'),
  school: Yup.string().required('School is required'),
  term: Yup.string().required('Term/Period is required'),
  overallGrade: Yup.string().required('Overall grade is required'),
  subjects: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      grade: Yup.string(),
      comments: Yup.string()
    })
  ),
  daysPresent: Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .min(0, 'Must be 0 or more'),
  daysAbsent: Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .min(0, 'Must be 0 or more'),
  strengths: Yup.string(),
  areasForImprovement: Yup.string(),
  generalComments: Yup.string(),
  extracurricular: Yup.string(),
  healthNotes: Yup.string(),
  recommendations: Yup.string(),
  createReminder: Yup.boolean(),
  reminderDueDate: Yup.string().when('createReminder', {
    is: true,
    then: (schema) => schema.required('Due date is required when creating reminder')
  }),
  assignTo: Yup.string().when('createReminder', {
    is: true,
    then: (schema) => schema.required('Please select who to assign the reminder to')
  }),
  reminderNote: Yup.string()
});

const CreateSchoolReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { beneficiary } = useBeneficiaryContext();
  const { organization, user } = useAuthContext();
  const { uploadDocument, deleteDocument, loading: uploadLoading } = useUpload();
  const { createSchoolReport, isCreating } = useCreateSchoolReport();
  const { updateSchoolReport, isUpdating } = useUpdateSchoolReport();
  const { createReminderAsync, isCreating: isCreatingReminder } = useCreateReminder();
  const { schoolStudents } = useSchoolStudents(id);
  const { users } = useOrganisationUsers();
  const { admins: branchAdmins } = useBranchAdmins();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Check if we're in edit mode
  const editData = location.state?.reportData;
  const isEditMode = !!editData;

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      reportDate: format(new Date(), 'yyyy-MM-dd'),
      school: '',
      term: '',
      overallGrade: '',
      subjects: [{ name: '', grade: '', comments: '' }],
      daysPresent: '',
      daysAbsent: '',
      strengths: '',
      areasForImprovement: '',
      generalComments: '',
      extracurricular: '',
      healthNotes: '',
      recommendations: '',
      createReminder: false,
      reminderDueDate: '',
      assignTo: user?.id || '',
      reminderNote: ''
    },
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit, watch, setValue, reset, getValues } = methods;

  // Unfinished new reports are kept in this browser, per user and child
  const draftScope = schoolReportDraftScope(user?.id, id);
  const { pendingDraft, draftSavedAt, continueDraft, discardDraft } =
    useFormDraft({
      store: schoolReportDraftStore,
      scope: draftScope,
      enabled: !isEditMode,
      values: watch(),
      reset,
      getValues
    });

  // Pre-fill form when editing
  useEffect(() => {
    if (editData) {
      // Parse the stored form data if available
      const formData = editData.form_data ?
        (typeof editData.form_data === 'string' ? JSON.parse(editData.form_data) : editData.form_data)
        : {};

      reset({
        reportDate: editData.report_date || format(new Date(), 'yyyy-MM-dd'),
        school: editData.school_id || '',
        term: formData.term || '',
        overallGrade: formData.overallGrade || '',
        subjects: formData.subjects?.length > 0 ? formData.subjects : [{ name: '', grade: '', comments: '' }],
        daysPresent: formData.daysPresent || '',
        daysAbsent: formData.daysAbsent || '',
        strengths: formData.strengths || '',
        areasForImprovement: formData.areasForImprovement || '',
        generalComments: editData.comment || formData.generalComments || '',
        extracurricular: formData.extracurricular || '',
        healthNotes: formData.healthNotes || '',
        recommendations: formData.recommendations || '',
        createReminder: false,
        reminderDueDate: '',
        assignTo: user?.id || '',
        reminderNote: ''
      });
    }
  }, [editData, reset, user?.id]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subjects'
  });

  const createReminder = watch('createReminder');
  const daysPresent = watch('daysPresent');
  const daysAbsent = watch('daysAbsent');

  const attendancePercentage = useMemo(() => {
    const present = parseInt(daysPresent) || 0;
    const absent = parseInt(daysAbsent) || 0;
    const total = present + absent;
    if (total === 0) return '-';
    return ((present / total) * 100).toFixed(1) + '%';
  }, [daysPresent, daysAbsent]);

  const schoolOptions = useMemo(() => {
    return (
      schoolStudents?.map(item => ({
        label: item.school?.school_name,
        value: item.school?.id
      })) || []
    );
  }, [schoolStudents]);

  const userOptions = useMemo(() => {
    return (
      users?.map(u => ({
        label: `${u.first_name} ${u.last_name}`,
        value: u.id
      })) || []
    );
  }, [users]);

  const onSubmit = async (values) => {
    setIsGeneratingPDF(true);
    try {
      // Find school name for PDF
      const selectedSchool = schoolStudents?.find(
        s => s.school?.id === values.school
      );
      const schoolName = selectedSchool?.school?.school_name || '';

      // Prepare data for PDF
      const pdfData = {
        ...values,
        schoolName,
        reporterName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
      };

      // Generate PDF
      const blob = await pdf(
        <SchoolReportPDF
          data={pdfData}
          beneficiary={beneficiary}
          organization={organization}
        />
      ).toBlob();

      // Create File object for upload
      const fileName = `${beneficiary?.first_name || 'beneficiary'}-school-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      const pdfFile = new File([blob], fileName, { type: 'application/pdf' });

      // Delete old PDF if updating
      if (isEditMode && editData.report_document) {
        await deleteDocument(editData.report_document);
      }

      // Upload PDF
      const reportUrl = await uploadDocument(pdfFile, 'school-reports');

      // Prepare form data to store for editing later
      const formDataToStore = {
        term: values.term,
        overallGrade: values.overallGrade,
        subjects: values.subjects,
        daysPresent: values.daysPresent,
        daysAbsent: values.daysAbsent,
        strengths: values.strengths,
        areasForImprovement: values.areasForImprovement,
        generalComments: values.generalComments,
        extracurricular: values.extracurricular,
        healthNotes: values.healthNotes,
        recommendations: values.recommendations
      };

      if (isEditMode) {
        // Update existing report
        await updateSchoolReport({
          id: editData.id,
          data: {
            school_id: values.school,
            user_id: user.id,
            reporter: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
            comment: values.generalComments || `${values.term} - ${values.overallGrade}`,
            report_date: values.reportDate,
            report_name: fileName,
            report_document: reportUrl,
            form_data: formDataToStore
          }
        });
        toast.success('School report updated successfully');
      } else {
        // Create new report
        await createSchoolReport({
          orphan_id: id,
          school_id: values.school,
          branch_id: organization.branch_id,
          organisation_id: organization.id,
          user_id: user.id,
          reporter: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
          comment: values.generalComments || `${values.term} - ${values.overallGrade}`,
          report_date: values.reportDate,
          report_name: fileName,
          report_document: reportUrl,
          form_data: formDataToStore
        });

        // Auto-create verification reminder for branch admin (1 week after creation)
        const verificationDueDate = format(addDays(new Date(), 7), 'yyyy-MM-dd');
        // Assign to first branch admin, or fall back to creator if no admins found
        const adminToAssign = branchAdmins?.[0]?.id || user?.id;
        try {
          await createReminderAsync({
            title: `Verify school report for ${beneficiary?.first_name || 'beneficiary'}`,
            description: `Please review and verify the school report created for ${beneficiary?.first_name} ${beneficiary?.last_name} (${values.term}). Created by ${user?.first_name} ${user?.last_name}.`,
            reminder_type: 'task',
            due_date: verificationDueDate,
            assigned_to_user_id: adminToAssign
          });
        } catch (reminderError) {
          console.error('Failed to create verification reminder:', reminderError);
          // Don't fail the whole operation if reminder creation fails
        }

        toast.success('School report created successfully');
        schoolReportDraftStore.clear(draftScope);
      }

      // Create optional reminder for next report if requested
      if (values.createReminder && values.reminderDueDate) {
        await createReminderAsync({
          title: `School report due for ${beneficiary?.first_name || 'beneficiary'}`,
          description: values.reminderNote || `Next school report is due for ${beneficiary?.first_name} ${beneficiary?.last_name}`,
          reminder_type: 'update',
          due_date: values.reminderDueDate,
          assigned_to_user_id: values.assignTo
        });
      }

      navigate(paths.beneficiaryShool.replace(':id', id));
    } catch (error) {
      console.error('Error saving school report:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} school report`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const isLoading = isGeneratingPDF || uploadLoading || isCreating || isUpdating || isCreatingReminder;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <DraftPrompt
        pendingDraft={pendingDraft}
        onContinue={continueDraft}
        onDiscard={discardDraft}
      />
      {/* Header */}
      <Card className="mb-3">
        <Card.Body>
          <Flex justifyContent="between" alignItems="center">
            <div>
              <h4 className="mb-1">{isEditMode ? 'Edit' : 'Create'} School Report</h4>
              <p className="text-muted mb-0">
                {beneficiary?.first_name} {beneficiary?.last_name}
                {beneficiary?.simple_id && ` (B${beneficiary.simple_id})`}
              </p>
            </div>
            <Button
              variant="falcon-secondary"
              onClick={() => navigate(paths.beneficiaryShool.replace(':id', id))}
            >
              <FontAwesomeIcon icon="arrow-left" className="me-2" />
              Back
            </Button>
          </Flex>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={8}>
          {/* Basic Information */}
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Report Information</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <RHFDatePicker name="reportDate" label="Report Date" required />
                </Col>
                <Col md={6}>
                  <RHFSelect
                    name="school"
                    label="School"
                    options={schoolOptions}
                    required
                  />
                </Col>
                <Col md={6}>
                  <RHFControl
                    name="term"
                    label="Term/Period"
                    placeholder="e.g., Term 1 2024"
                    required
                  />
                </Col>
                <Col md={6}>
                  <RHFSelect
                    name="overallGrade"
                    label="Overall Performance"
                    options={gradeOptions}
                    required
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Subjects & Grades */}
          <Card className="mb-3">
            <Card.Header>
              <Flex justifyContent="between" alignItems="center">
                <h5 className="mb-0">Subjects & Grades</h5>
                <Button
                  variant="falcon-primary"
                  size="sm"
                  onClick={() => append({ name: '', grade: '', comments: '' })}
                >
                  <FontAwesomeIcon icon="plus" className="me-1" />
                  Add Subject
                </Button>
              </Flex>
            </Card.Header>
            <Card.Body>
              {fields.map((field, index) => (
                <Row key={field.id} className="g-2 mb-3 align-items-end">
                  <Col md={3}>
                    <RHFControl
                      name={`subjects.${index}.name`}
                      label={index === 0 ? 'Subject' : ''}
                      placeholder="Subject name"
                    />
                  </Col>
                  <Col md={2}>
                    <RHFControl
                      name={`subjects.${index}.grade`}
                      label={index === 0 ? 'Grade' : ''}
                      placeholder="Grade"
                    />
                  </Col>
                  <Col md={6}>
                    <RHFControl
                      name={`subjects.${index}.comments`}
                      label={index === 0 ? 'Comments' : ''}
                      placeholder="Teacher comments"
                    />
                  </Col>
                  <Col md={1}>
                    {fields.length > 1 && (
                      <Button
                        variant="falcon-danger"
                        size="sm"
                        onClick={() => remove(index)}
                      >
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
            </Card.Body>
          </Card>

          {/* Attendance */}
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Attendance</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <RHFControl
                    name="daysPresent"
                    label="Days Present"
                    type="number"
                    min={0}
                  />
                </Col>
                <Col md={4}>
                  <RHFControl
                    name="daysAbsent"
                    label="Days Absent"
                    type="number"
                    min={0}
                  />
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Attendance Rate</Form.Label>
                    <div className="form-control bg-light" style={{ fontSize: '1.1rem' }}>
                      {attendancePercentage}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Teacher Feedback */}
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Teacher Feedback</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={12}>
                  <RHFControl
                    name="strengths"
                    label="Strengths"
                    as="textarea"
                    rows={3}
                    placeholder="Student's strengths and positive qualities..."
                  />
                </Col>
                <Col md={12}>
                  <RHFControl
                    name="areasForImprovement"
                    label="Areas for Improvement"
                    as="textarea"
                    rows={3}
                    placeholder="Areas where student can improve..."
                  />
                </Col>
                <Col md={12}>
                  <RHFControl
                    name="generalComments"
                    label="General Comments"
                    as="textarea"
                    rows={3}
                    placeholder="Overall comments about the student..."
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Additional Information */}
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Additional Information</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={12}>
                  <RHFControl
                    name="extracurricular"
                    label="Extracurricular Activities"
                    as="textarea"
                    rows={2}
                    placeholder="Sports, clubs, activities..."
                  />
                </Col>
                <Col md={12}>
                  <RHFControl
                    name="healthNotes"
                    label="Health/Wellbeing Notes"
                    as="textarea"
                    rows={2}
                    placeholder="Any health or wellbeing observations..."
                  />
                </Col>
                <Col md={12}>
                  <RHFControl
                    name="recommendations"
                    label="Recommendations"
                    as="textarea"
                    rows={2}
                    placeholder="Recommendations for next term..."
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <div className="sticky-top" style={{ top: '1rem' }}>
            {/* Reminder Section */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon="bell" className="me-2" />
                  Reminder for Next Report
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Check
                  type="switch"
                  id="createReminder"
                  label="Create reminder for next report"
                  checked={createReminder}
                  onChange={(e) => setValue('createReminder', e.target.checked)}
                  className="mb-3"
                />

                {createReminder && (
                  <>
                    <RHFDatePicker
                      name="reminderDueDate"
                      label="Due Date"
                      required
                      max={false}
                    />
                    <div className="mt-3">
                      <RHFSelect
                        name="assignTo"
                        label="Assign To"
                        options={userOptions}
                        required
                      />
                    </div>
                    <div className="mt-3">
                      <RHFControl
                        name="reminderNote"
                        label="Note (Optional)"
                        as="textarea"
                        rows={2}
                        placeholder="Additional notes for the reminder..."
                      />
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Info about auto-verification reminder */}
            {!isEditMode && (
              <Card className="mb-3 bg-light">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    <FontAwesomeIcon icon="info-circle" className="me-1" />
                    A verification reminder will be automatically created for 1 week after submission.
                  </small>
                </Card.Body>
              </Card>
            )}

            {/* Submit Button */}
            <Card>
              <Card.Body>
                <LoadingButton
                  variant="primary"
                  type="submit"
                  className="w-100"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon="file-pdf" className="me-2" />
                  {isEditMode ? 'Update Report' : 'Generate Report'}
                </LoadingButton>
                <p className="text-muted small mt-2 mb-0 text-center">
                  This will generate a PDF report and save it to the system
                </p>
                {!isEditMode && draftSavedAt && (
                  <p className="small mt-1 mb-0 text-center">
                    <DraftSavedNote savedAt={draftSavedAt} />
                  </p>
                )}
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </FormProvider>
  );
};

export default CreateSchoolReportPage;
